import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Theme } from 'redux/reducers/rootReducer';
import { MultilingualType, OrderTicket } from 'types';

import i18n from '../../../assets/languages/configs';
import DisplayCustomNumbers from '../../DisplayCustomNumbers/DisplayCustomNumbers';
import FujiModal from '../../fuji-modal/FujiModal';
import { kanaFullToHalf, getValueByLanguage } from '../../../utils';

import styles from './styles.module.css';

interface BottomContentProps {
    orderTicket: OrderTicket;
    currentTheme: Theme;
    circleSize: number;
    isSameDeviceId: boolean;
    terminateTicket: (orderTicket: OrderTicket) => Promise<void>;
}

const BottomContent: React.FC<BottomContentProps> = ({
    orderTicket,
    currentTheme,
    circleSize,
    isSameDeviceId,
    terminateTicket,
}) => {
    const { t } = useTranslation();
    const [isOpenEndUseModal, setIsOpenEndUseModal] = useState(false);
    const { colorFontBottom } = currentTheme;
    const { reservationInfo } = orderTicket?.orderUsageTickets?.[0] ?? {};
    const { userForm } = reservationInfo ?? {};
    const { language } = i18n;
    const l = useCallback((text?: MultilingualType | null) => getValueByLanguage(text, language), [language]);

    const handleOpenEndUseModal = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setIsOpenEndUseModal(true);
    };

    const handleCloseEndUseModal = () => {
        setIsOpenEndUseModal(false);
    };

    const handleConfirmEndUse = () => {
        setIsOpenEndUseModal(false);
        void terminateTicket(orderTicket);
    };

    const terminateButtonConfirmNote = useMemo<string>(() => {
        return l(orderTicket.terminateButtonConfirmNote) !== ''
            ? l(orderTicket.terminateButtonConfirmNote)
            : t('ticket-display.end-use-confirmation');
    }, [orderTicket.terminateButtonConfirmNote]);

    return (
        <>
            {
                <div className={styles.infoArea}>
                    {userForm?.representativeName && (
                        <div
                            style={{
                                color: colorFontBottom,
                                fontSize: circleSize * 0.1,
                                textOverflow: 'clip',
                                overflow: 'hidden',
                                paddingLeft: '10px',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {kanaFullToHalf(userForm?.representativeName)}
                        </div>
                    )}
                    <DisplayCustomNumbers
                        orderTicket={orderTicket}
                        circleSize={circleSize * 0.08}
                        textColor={colorFontBottom}
                    />
                    {orderTicket?.isTerminateButtonEnabled && isSameDeviceId && (
                        <div className={styles.extraButtonContainer}>
                            <button type="button" className={styles.extraButton} onClick={handleOpenEndUseModal}>
                                {t('ticket-display.end-use')}
                            </button>
                        </div>
                    )}
                </div>
            }
            <FujiModal
                isOpen={isOpenEndUseModal}
                onCancel={handleCloseEndUseModal}
                txtCancel={t('ticket-display.end-use-cancel') ?? ''}
                onSubmit={handleConfirmEndUse}
                txtSubmit={t('ticket-display.end-use-submit')}
                body={terminateButtonConfirmNote}
            />
        </>
    );
};

export default memo(BottomContent);
